import React, { useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { Button, Checkbox, Edit01, Modal } from "@opendate-app/setlist";
import AddOnMessageTemplates from "../../AddOnMessageTemplates/components/AddOnMessageTemplates";
import { toastError, toastSuccess } from "../../../shared/toastHelper";
import { EditorState } from "draft-js";
import { stateToHTML } from "draft-js-export-html";
import { stateFromHTML } from "draft-js-import-html";
import RichEditor from "../../RichEditor/components/RichEditor";

const COPY_FIELDS = [
  { flag: "hasAddOnsIntro", text: "addOnsIntro", defaultKey: "intro", kind: "intro", label: "Add-ons intro", title: "Add-ons Intro" },
  { flag: "hasAddOnsAlertMessage", text: "addOnsAlertMessage", defaultKey: "alertMessage", kind: "alert", label: "Alert Message", title: "Alert Message" },
];

const errorText = (error) => {
  const messages = error && error.response && error.response.data;
  return Array.isArray(messages) ? messages.join(", ") : "Something went wrong. Try again.";
};

const AddOnsCopySection = ({ settings, defaults = {}, templates, templatesUrl, csrfToken, onChange }) => {
  const [editing, setEditing] = useState(null);
  const [editorState, setEditorState] = useState(() => EditorState.createEmpty());
  // The confirm's saved intro and alert templates: the same team-wide list, saved and deleted the same way.
  const [templateLists, setTemplateLists] = useState(() => templates || { intro: [], alert: [] });
  const [drafts, setDrafts] = useState({});
  const headers = { headers: { "X-CSRF-TOKEN": csrfToken } };

  const createTemplate = (_csrfToken, _team, kind, draft, body, onSuccess) =>
    axios
      .post(templatesUrl, { add_on_message_template: { title: draft.title, body, kind } }, headers)
      .then(({ data }) => {
        setTemplateLists((current) => ({ ...current, [kind]: [...(current[kind] || []), data] }));
        setDrafts((current) => ({ ...current, [kind]: {} }));
        if (onSuccess) onSuccess(data);
        toastSuccess("Template has been saved");
      })
      .catch((error) => toastError(errorText(error)));

  const deleteTemplate = (_csrfToken, _team, template) =>
    axios
      .delete(`${templatesUrl}/${template.id}`, headers)
      .then(() =>
        setTemplateLists((current) => ({
          intro: (current.intro || []).filter((candidate) => candidate.id !== template.id),
          alert: (current.alert || []).filter((candidate) => candidate.id !== template.id),
        })),
      )
      .catch((error) => toastError(errorText(error)));

  const startEditing = (field) => {
    setEditorState(EditorState.createWithContent(stateFromHTML(settings[field.text] || defaults[field.defaultKey] || "")));
    setEditing(field);
  };

  return (
    <div className="series-add-ons-copy" data-testid="add-ons-copy">
      {COPY_FIELDS.map((field) => (
        <div key={field.flag} className="series-add-ons-copy-item">
          <Checkbox
            id={`series-${field.flag}`}
            label={field.label}
            checked={Boolean(settings[field.flag])}
            onChange={(event) => onChange({ [field.flag]: event.target.checked })}
          />
          <Button
            variant="tertiary"
            size="sm"
            icon={Edit01}
            aria-label={`Edit ${field.label.toLowerCase()}`}
            disabled={!settings[field.flag]}
            onClick={() => startEditing(field)}
          />
        </div>
      ))}
      <Modal
        open={Boolean(editing)}
        width={840}
        closeOnBackdrop={false}
        title={editing ? editing.title : ""}
        onClose={() => setEditing(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                onChange({ [editing.text]: stateToHTML(editorState.getCurrentContent()) });
                setEditing(null);
              }}
            >
              Save
            </Button>
          </>
        }
      >
        {editing && templatesUrl && (
          <div style={{ marginBottom: "16px" }}>
            <AddOnMessageTemplates
              csrfToken={csrfToken || ""}
              team={{}}
              kind={editing.kind}
              editorState={editorState}
              onEditorStateChange={setEditorState}
              templates={templateLists[editing.kind] || []}
              draft={drafts[editing.kind] || {}}
              draftChanged={(kind, draft) => setDrafts((current) => ({ ...current, [kind]: draft }))}
              createTemplate={createTemplate}
              deleteTemplate={deleteTemplate}
            />
          </div>
        )}
        {editing && <RichEditor editorState={editorState} onEditorStateChange={setEditorState} />}
      </Modal>
    </div>
  );
};

AddOnsCopySection.propTypes = {
  settings: PropTypes.object.isRequired,
  defaults: PropTypes.object,
  templates: PropTypes.shape({ intro: PropTypes.array, alert: PropTypes.array }),
  templatesUrl: PropTypes.string,
  csrfToken: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

export default AddOnsCopySection;
